// CB:BEGIN settings-hook
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { getApiUrl } from '../lib/getApiUrl';

interface UserSettings {
  directContactEnabled: boolean;
  maskContactInfo: boolean;
  hideAddressUntilBidAccepted: boolean;
  verifiedContractorsOnlyMessaging: boolean;
  shareInsuranceClaimInfo: boolean;
  allowFinancingOffers: boolean;
}

interface NotificationPrefs {
  emailEnabled: boolean;
  smsEnabled: boolean;
  inAppEnabled: boolean;
  perEvent: Record<string, any> | null;
}

interface ContractorSettings {
  serviceAreaType: 'RADIUS' | 'ZIP_LIST' | 'COUNTY_LIST';
  bidRadiusMiles: number;
  serviceZipCodes: string[] | null;
  serviceCounties: string[] | null;
  quietHoursStart: string | null;
  quietHoursEnd: string | null;
  allowCalls: boolean;
  allowTexts: boolean;
  preferredContactChannel: 'IN_APP' | 'PHONE' | 'SMS';
  autoAcceptPlatformMessagesBusinessHours: boolean;
  pauseBidding: boolean;
  leadTypesAllowed: Record<string, boolean> | null;
}

interface AdminSettings {
  escalationSlaMinutes: number;
  escalationThresholdCount: number;
  onCallEmail: string | null;
  onCallSms: string | null;
  notifyOnSecurityAlerts: boolean;
  requireTwoPersonApprovalForOverrides: boolean;
  lockSensitiveActionsToAllowlistedIps: boolean;
}

interface SettingsData {
  role: string;
  userSettings: UserSettings;
  notificationPrefs: NotificationPrefs;
  contractorSettings?: ContractorSettings;
  adminSettings?: AdminSettings;
  adminFeaturePermissions?: any;
}

// Module-level cache
let cachedData: SettingsData | null = null;
let cachePromise: Promise<SettingsData> | null = null;

// Shallow compare for dirty detection
function shallowEqual(obj1: any, obj2: any): boolean {
  if (obj1 === obj2) return true;
  if (!obj1 || !obj2) return false;
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) return false;
  for (const key of keys1) {
    if (obj1[key] !== obj2[key]) return false;
  }
  return true;
}

// Deep merge helper
function deepMerge(target: any, source: any): any {
  const output = { ...target };
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach(key => {
      if (isObject(source[key])) {
        if (!(key in target)) {
          Object.assign(output, { [key]: source[key] });
        } else {
          output[key] = deepMerge(target[key], source[key]);
        }
      } else {
        Object.assign(output, { [key]: source[key] });
      }
    });
  }
  return output;
}

function isObject(item: any): boolean {
  return item && typeof item === 'object' && !Array.isArray(item);
}

export function useCoreBidsSettings() {
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(!cachedData);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [serverSnapshot, setServerSnapshot] = useState<SettingsData | null>(cachedData);
  const [localDraft, setLocalDraft] = useState<SettingsData | null>(cachedData);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Compute dirty state
  const dirty = useCallback(() => {
    if (!serverSnapshot || !localDraft) return false;
    
    // Check each relevant sub-object
    if (!shallowEqual(serverSnapshot.userSettings, localDraft.userSettings)) return true;
    if (!shallowEqual(serverSnapshot.notificationPrefs, localDraft.notificationPrefs)) return true;
    if (serverSnapshot.contractorSettings && localDraft.contractorSettings) {
      if (!shallowEqual(serverSnapshot.contractorSettings, localDraft.contractorSettings)) return true;
    }
    if (serverSnapshot.adminSettings && localDraft.adminSettings) {
      if (!shallowEqual(serverSnapshot.adminSettings, localDraft.adminSettings)) return true;
    }
    
    return false;
  }, [serverSnapshot, localDraft]);

  // Compute dirty keys
  const dirtyKeys = useCallback(() => {
    if (!serverSnapshot || !localDraft) return [];
    const keys: string[] = [];
    
    if (!shallowEqual(serverSnapshot.userSettings, localDraft.userSettings)) {
      keys.push('userSettings');
    }
    if (!shallowEqual(serverSnapshot.notificationPrefs, localDraft.notificationPrefs)) {
      keys.push('notificationPrefs');
    }
    if (serverSnapshot.contractorSettings && localDraft.contractorSettings) {
      if (!shallowEqual(serverSnapshot.contractorSettings, localDraft.contractorSettings)) {
        keys.push('contractorSettings');
      }
    }
    if (serverSnapshot.adminSettings && localDraft.adminSettings) {
      if (!shallowEqual(serverSnapshot.adminSettings, localDraft.adminSettings)) {
        keys.push('adminSettings');
      }
    }
    
    return keys;
  }, [serverSnapshot, localDraft]);

  // Validate notification prefs
  const validateNotificationPrefs = useCallback((prefs: NotificationPrefs): string | null => {
    if (!prefs.emailEnabled && !prefs.smsEnabled && !prefs.inAppEnabled) {
      return 'At least one notification channel must be enabled';
    }
    return null;
  }, []);

  // Load settings
  const load = useCallback(async (force = false) => {
    if (cachedData && !force && cachePromise) {
      return cachePromise;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    const loadPromise = (async () => {
      try {
        setLoading(true);
        setError(null);
        
        const token = await getToken();
        if (!token) {
          throw new Error('Authentication required');
        }

        const response = await fetch(getApiUrl('/api/me/settings'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          signal: abortControllerRef.current!.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to load settings: ${response.status}`);
        }

        const data = await response.json();
        
        // Set defaults if missing
        const settingsData: SettingsData = {
          role: data.role || 'HOMEOWNER',
          userSettings: data.userSettings || {
            directContactEnabled: false,
            maskContactInfo: true,
            hideAddressUntilBidAccepted: true,
            verifiedContractorsOnlyMessaging: true,
            shareInsuranceClaimInfo: false,
            allowFinancingOffers: true,
          },
          notificationPrefs: data.notificationPrefs || {
            emailEnabled: true,
            smsEnabled: false,
            inAppEnabled: true,
            perEvent: null,
          },
          ...(data.contractorSettings && { contractorSettings: data.contractorSettings }),
          ...(data.adminSettings && { adminSettings: data.adminSettings }),
          ...(data.adminFeaturePermissions && { adminFeaturePermissions: data.adminFeaturePermissions }),
        };

        // Update cache
        cachedData = settingsData;
        setServerSnapshot(settingsData);
        setLocalDraft(settingsData);
        setError(null);
        
        return settingsData;
      } catch (err: any) {
        if (err.name === 'AbortError') {
          return cachedData || null;
        }
        console.error('Error loading settings:', err);
        setError(err.message || 'Failed to load settings');
        throw err;
      } finally {
        setLoading(false);
        cachePromise = null;
      }
    })();

    cachePromise = loadPromise;
    return loadPromise;
  }, [getToken]);

  // Initial load
  useEffect(() => {
    if (!cachedData) {
      load();
    } else {
      setServerSnapshot(cachedData);
      setLocalDraft(cachedData);
      setLoading(false);
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [load]);

  // Update local draft (optimistic)
  const updateLocal = useCallback((partial: Partial<SettingsData>) => {
    setLocalDraft(prev => {
      if (!prev) return prev;
      return deepMerge(prev, partial);
    });
  }, []);

  // Save to server
  const save = useCallback(async (partial?: Partial<SettingsData>) => {
    if (!localDraft) {
      throw new Error('No settings data to save');
    }

    // Merge partial if provided
    const toSave = partial ? deepMerge(localDraft, partial) : localDraft;

    // Validate notification prefs
    const validationError = validateNotificationPrefs(toSave.notificationPrefs);
    if (validationError) {
      setError(validationError);
      throw new Error(validationError);
    }

    setSaving(true);
    setError(null);

    try {
      const token = await getToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      // Build payload
      const payload: any = {};
      if (toSave.userSettings) payload.userSettings = toSave.userSettings;
      if (toSave.notificationPrefs) payload.notificationPrefs = toSave.notificationPrefs;
      if (toSave.contractorSettings) payload.contractorSettings = toSave.contractorSettings;
      if (toSave.adminSettings) payload.adminSettings = toSave.adminSettings;

      const response = await fetch(getApiUrl('/api/me/settings'), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to save settings');
      }

      const savedData = await response.json();
      
      // Merge response back
      const mergedData: SettingsData = {
        role: savedData.role || toSave.role,
        userSettings: savedData.userSettings || toSave.userSettings,
        notificationPrefs: savedData.notificationPrefs || toSave.notificationPrefs,
        ...(savedData.contractorSettings && { contractorSettings: savedData.contractorSettings }),
        ...(savedData.adminSettings && { adminSettings: savedData.adminSettings }),
        ...(savedData.adminFeaturePermissions && { adminFeaturePermissions: savedData.adminFeaturePermissions }),
      };

      // Update cache and state
      cachedData = mergedData;
      setServerSnapshot(mergedData);
      setLocalDraft(mergedData);
      setLastSavedAt(new Date());
      
      // Show success message briefly
      setError('✅ Settings saved successfully');
      setTimeout(() => setError(null), 3000);
      
      return mergedData;
    } catch (err: any) {
      console.error('Error saving settings:', err);
      const errorMsg = err.message || 'Failed to save settings';
      setError(errorMsg);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [localDraft, getToken, validateNotificationPrefs]);

  // Reset local to server
  const resetLocalToServer = useCallback(() => {
    if (serverSnapshot) {
      setLocalDraft(serverSnapshot);
      setError(null);
    }
  }, [serverSnapshot]);

  // Refresh from server
  const refresh = useCallback(() => {
    return load(true);
  }, [load]);

  return {
    data: localDraft,
    loading,
    error,
    refresh,
    updateLocal,
    save,
    dirty: dirty(),
    dirtyKeys: dirtyKeys(),
    saving,
    lastSavedAt,
    resetLocalToServer,
  };
}
// CB:END settings-hook

